"use client";

import { create } from "zustand";
import type { TranscriptLine, Recommendation } from "@/lib/types";

interface CallState {
  callMode: "meet" | "phone";
  currentSpeaker: "rep" | "customer";
  elapsed: number;
  running: boolean;
  transcript: TranscriptLine[];
  recommendation: Recommendation | null;
  isCoaching: boolean;
  
  // Real-time metrics
  waveform: number[];

  // Actions
  toggleRunning: () => void;
  reset: () => void;
  addTranscriptLine: (line: Omit<TranscriptLine, "id" | "at">) => void;
  setRecommendation: (rec: Recommendation) => void;
  setCoaching: (isCoaching: boolean) => void;
  setElapsed: (elapsed: number) => void;
  updateWaveform: (active: boolean) => void;
  setCallMode: (mode: "meet" | "phone") => void;
  setCurrentSpeaker: (speaker: "rep" | "customer") => void;
}

function initial() {
  return {
    callMode: "meet" as const,
    currentSpeaker: "rep" as const,
    elapsed: 0,
    running: false,
    transcript: [] as TranscriptLine[],
    recommendation: null,
    isCoaching: false,
    waveform: Array.from({ length: 48 }, () => 0.1),
  };
}

export const useCallStore = create<CallState>((set, get) => ({
  ...initial(),

  toggleRunning: () => set((s) => ({ running: !s.running })),

  reset: () => set({ ...initial() }),

  addTranscriptLine: (line) => {
    set((s) => ({
      transcript: [
        ...s.transcript,
        {
          ...line,
          id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          at: s.elapsed,
        },
      ],
    }));
  },

  setRecommendation: (rec) => set({ recommendation: rec, isCoaching: false }),

  setCoaching: (isCoaching) => set({ isCoaching }),

  setElapsed: (elapsed) => set({ elapsed }),

  updateWaveform: (active) => {
    set((s) => {
      // Very simple simulated waveform based on microphone active state
      return {
        waveform: s.waveform.map((_, i) => {
          if (!active) return 0.1;
          const base = Math.random();
          const envelope = 0.35 + 0.45 * Math.abs(Math.sin((i / s.waveform.length) * Math.PI));
          return Math.min(1, Math.max(0.1, base * envelope + 0.12));
        }),
      };
    });
  },

  setCallMode: (mode) => set({ callMode: mode, currentSpeaker: "rep" }),
  
  setCurrentSpeaker: (speaker) => set({ currentSpeaker: speaker }),
}));
